// Replay = the minimum data set to deterministically reproduce a match.
//
// `inputs[tick]` is the merged human + bot input array that was fed into
// `tickMatch` at that tick. We record merged inputs (rather than just human
// inputs and re-running bots at playback) so that future bot-logic changes do
// not invalidate sim-bug replays. The CLI side stays bot-agnostic.
//
// The shape is plain JSON-friendly POD: array index = tick, value = inputs or
// null when no inputs fired that tick. Long runs of nulls compress well.
//
// `setup` (optional) lets demo/test authors hand-place the puck and players
// before tick 0, skipping the faceoff. Applied after `createMatch(seed)` and
// before any `tickMatch` call. Everything downstream is still deterministic
// from `seed + setup + inputs`.

import type { PlayerInput } from "@arcade-hockey/shared";
import { applySetup, createMatch, tickMatch } from "./core/SimTick.js";
import type { RosterSizes } from "./state/initial.js";
import { TUNING_HASH } from "./state/tuning.js";
import type { MatchState, ReplaySetup } from "./state/types.js";

export type Replay = {
  description: string;
  recordedAt: string; // ISO timestamp; stamped at serialization time, not by sim
  gitSha?: string;
  seed: number;
  tuningHash: number;
  tickCount: number;
  finalHash: number;
  setup?: ReplaySetup;
  // Per-team skater count the recording was made at. Optional so replays
  // captured before this field existed (or against the default 5v5 roster)
  // round-trip unchanged; absent → createMatch's default applies (5v5).
  rosterSizes?: RosterSizes;
  inputs: ReadonlyArray<ReadonlyArray<PlayerInput> | null>;
};

// A blank Replay ready to accumulate ticks from a live match. The recorder on
// the client side calls `recordTick(replay, tick, mergedInputs)` once per tick
// and then `finalizeReplay(replay, finalState)` when it's done.
//
// `rosterSizes` is stamped at construction so a finalized Replay carries the
// roster the match ran at. Omit to default to 5v5 (createMatch's default).
export function emptyReplay(
  seed: number,
  description = "",
  rosterSizes?: RosterSizes,
): MutableReplay {
  return {
    description,
    recordedAt: "",
    seed,
    tuningHash: TUNING_HASH,
    tickCount: 0,
    finalHash: 0,
    rosterSizes,
    inputs: [],
  };
}

// MutableReplay is the in-flight version used during recording. Once handed to
// `finalizeReplay` it should be treated as immutable (typed as Replay).
export type MutableReplay = {
  description: string;
  recordedAt: string;
  gitSha?: string;
  seed: number;
  tuningHash: number;
  tickCount: number;
  finalHash: number;
  setup?: ReplaySetup;
  rosterSizes?: RosterSizes;
  inputs: Array<Array<PlayerInput> | null>;
};

// Append a tick's merged inputs to the recorder. The sim's tick loop and the
// bots package both reuse pooled PlayerInput objects, so we per-field copy
// every input here — storing by reference would alias the next tick's data
// over this tick's. PlayerInput is POD; the copy is cheap.
export function recordTick(
  replay: MutableReplay,
  tick: number,
  inputs: ReadonlyArray<PlayerInput>,
): void {
  while (replay.inputs.length <= tick) replay.inputs.push(null);
  if (inputs.length === 0) {
    replay.inputs[tick] = null;
  } else {
    const copy: Array<PlayerInput> = new Array(inputs.length);
    for (let i = 0; i < inputs.length; i++) {
      const src = inputs[i];
      if (src) copy[i] = clonePlayerInput(src);
    }
    replay.inputs[tick] = copy;
  }
  replay.tickCount = Math.max(replay.tickCount, tick + 1);
}

function clonePlayerInput(src: PlayerInput): PlayerInput {
  return {
    tick: src.tick,
    seq: src.seq,
    seatId: src.seatId,
    playerId: src.playerId,
    moveX: src.moveX,
    moveY: src.moveY,
    sprint: src.sprint,
    buttons: src.buttons,
  };
}

// Stamp final metadata on the replay. After this, treat it as a Replay
// (immutable).
export function finalizeReplay(
  replay: MutableReplay,
  finalState: MatchState,
  description?: string,
): Replay {
  replay.tickCount = Math.max(replay.tickCount, finalState.tick);
  replay.finalHash = finalState.hash;
  if (description !== undefined) replay.description = description;
  return replay as Replay;
}

export type ReplayRunResult = {
  finalHash: number;
  perTickHashes: ReadonlyArray<number>;
  divergedAtTick: number | null; // index where perTickHashes first differs from a hand-built reference; null when no reference is provided
};

// Run a replay through the sim and return the resulting hash trajectory. This
// is the "playback" side of the contract used by the CLI test harness.
//
// The returned `perTickHashes[tick]` is the hash AFTER `tickMatch` is called
// for that tick. `finalHash` is the hash after the last tick.
export function runReplay(replay: Replay): ReplayRunResult {
  const state = createMatch({ seed: replay.seed, rosterSizes: replay.rosterSizes });
  if (replay.setup !== undefined) applySetup(state, replay.setup);
  const perTickHashes: number[] = [];
  for (let tick = 0; tick < replay.tickCount; tick++) {
    const inputs = replay.inputs[tick] ?? EMPTY_INPUTS;
    tickMatch(state, inputs);
    perTickHashes.push(state.hash);
  }
  return { finalHash: state.hash, perTickHashes, divergedAtTick: null };
}

const EMPTY_INPUTS: ReadonlyArray<PlayerInput> = [];

// JSON parse + stringify. Pure (no fs); the CLI's loadReplayFromFile wraps
// this with readFileSync, the client's overlay wraps it with the Clipboard
// API. A tuning-hash mismatch is a *warning* (returned in `warnings`) — old
// replays should still play back, just with the caveat that the sim has been
// re-balanced under them.
export type ParseOutcome = {
  replay: Replay;
  warnings: string[];
};

export function parseReplay(json: string): ParseOutcome {
  const raw = JSON.parse(json) as Partial<Replay>;
  if (typeof raw !== "object" || raw === null) {
    throw new Error("replay: expected a JSON object at top level");
  }
  if (typeof raw.seed !== "number") throw new Error("replay: seed must be a number");
  if (typeof raw.tickCount !== "number") throw new Error("replay: tickCount must be a number");
  if (typeof raw.finalHash !== "number") throw new Error("replay: finalHash must be a number");
  if (typeof raw.tuningHash !== "number") throw new Error("replay: tuningHash must be a number");
  if (!Array.isArray(raw.inputs)) throw new Error("replay: inputs must be an array");
  if (raw.setup !== undefined && (typeof raw.setup !== "object" || raw.setup === null)) {
    throw new Error("replay: setup must be an object when present");
  }
  if (raw.rosterSizes !== undefined) {
    if (typeof raw.rosterSizes !== "object" || raw.rosterSizes === null) {
      throw new Error("replay: rosterSizes must be an object when present");
    }
    const rs = raw.rosterSizes as Partial<RosterSizes>;
    if (typeof rs.home !== "number" || typeof rs.away !== "number") {
      throw new Error("replay: rosterSizes.home and rosterSizes.away must be numbers");
    }
  }

  const warnings: string[] = [];
  if (raw.tuningHash !== TUNING_HASH) {
    warnings.push(
      `replay: tuningHash mismatch (recorded=0x${raw.tuningHash.toString(16).padStart(8, "0")}, current=0x${TUNING_HASH.toString(16).padStart(8, "0")}); expect divergence`,
    );
  }

  return { replay: raw as Replay, warnings };
}

export function serializeReplay(replay: Replay): string {
  return JSON.stringify(replay, null, 2);
}
